import React from 'react';

import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useRecentlyViewedCarouselEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';

import { SpecialsCarousel, StaffPicksCarousel, TopSellersCarousel } from 'src/components/carousels/generic-carousels';
import { RecentlyViewedCarousel } from 'src/components/carousels/products-carousels';
import { SpecialOffersCarousel } from 'src/components/carousels/offers-carousels';

import { MenuSectionType } from 'src/gql/graphql';
import type { ProductCarouselType } from './carousel-types';

/**
 * Per-type renderers for the Product Carousel block.
 *
 * Only the types whose contents the platform decides are wired up (ECOMM-12683).
 * They need no author configuration beyond the heading, so nothing here reads a
 * type-specific field — that arrives with the category/brand/collection/custom
 * tickets, along with the conditional-field machinery those need.
 *
 * Kept apart from carousel-types.ts so that module stays pure data plus pure
 * functions, testable with no React or hook mocking.
 */

/**
 * What the harness hands a type renderer. `heading` is already resolved (authored
 * beats generated) and interpolated; `undefined` means the author turned the
 * heading off, which is distinct from "there is no heading for this type".
 */
export type ProductCarouselRenderProps = { heading: string | undefined };

/**
 * Every carousel below takes the same pair: an optional `title` override and the
 * existing `showTitle` opt-out (`showTitle === false` is what each one already
 * checks). Passing `title` while hiding it is harmless, so both derive from the
 * single resolved heading.
 */
function headingProps(heading: string | undefined): { title: string | undefined; showTitle: boolean } {
  return { title: heading, showTitle: heading !== undefined };
}

function RecentlyViewedRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  const { isLoggedIn } = useUser();
  const { isKiosk } = useUI();
  const personalizationEnabled = useRecentlyViewedCarouselEnabled();
  // Recently Viewed is personal to a signed-in shopper, and a shared kiosk has no
  // meaningful "you". Guards carried over from the standalone RecentlyViewedSection block.
  if (isKiosk || !personalizationEnabled || !isLoggedIn) {
    return <></>;
  }
  return <RecentlyViewedCarousel {...headingProps(heading)} />;
}

function ItemsOnSaleRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  const { dispensary } = useDispensary();
  // Some stores are configured to never show discounts to shoppers; a carousel
  // built entirely from discounted products has to respect that.
  const hideSpecials = useHideDiscountFromConsumer(dispensary);
  if (hideSpecials) {
    return <></>;
  }
  return <SpecialsCarousel {...headingProps(heading)} />;
}

function TopSellersRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  return <TopSellersCarousel {...headingProps(heading)} />;
}

function StaffPicksRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  return <StaffPicksCarousel {...headingProps(heading)} />;
}

function OffersRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  return <SpecialOffersCarousel {...headingProps(heading)} />;
}

export const PRODUCT_CAROUSEL_RENDERERS: Partial<
  Record<ProductCarouselType, React.ComponentType<ProductCarouselRenderProps>>
> = {
  [MenuSectionType.recentlyViewed]: RecentlyViewedRender,
  [MenuSectionType.specials]: ItemsOnSaleRender,
  [MenuSectionType.topSellers]: TopSellersRender,
  [MenuSectionType.staffPicks]: StaffPicksRender,
  [MenuSectionType.offers]: OffersRender,
};

/**
 * The renderer for a stored `carouselType`, or `undefined` when that type has none
 * yet. Same own-property guard as `carouselTypeDefinition`: saved page data is JSON
 * a hand-edit can author, and a stored `constructor` must resolve to nothing rather
 * than to something off `Object.prototype`.
 */
export function carouselTypeRenderer(
  carouselType: string
): React.ComponentType<ProductCarouselRenderProps> | undefined {
  return Object.prototype.hasOwnProperty.call(PRODUCT_CAROUSEL_RENDERERS, carouselType)
    ? PRODUCT_CAROUSEL_RENDERERS[carouselType as ProductCarouselType]
    : undefined;
}
